import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Key,
  Lock,
  Smartphone,
  DoorOpen,
  Building2,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Shield,
  FileText,
  AlertCircle,
} from 'lucide-react';
import type { CheckinMethodType } from '@/types/database';
import { formatGuestTime } from './guestDates';

interface SelfCheckInCardProps {
  // Check-in timing
  checkInTime: string | null;
  // Method and codes
  selfCheckinEnabled: boolean;
  checkinMethod: CheckinMethodType | null;
  lockboxLocation: string | null;
  lockboxCode: string | null;
  keypadCode: string | null;
  smartLockProvider: string | null;
  smartLockInstructions: string | null;
  keyLocation: string | null;
  // Building access
  entranceInstructions: string | null;
  doorCode: string | null;
  gateCode: string | null;
  buildingAccessInfo: string | null;
  floorApartmentInfo: string | null;
  // General check-in instructions (from CheckInCard)
  checkInInstructions?: string | null;
  // Check-out
  checkOutTime?: string | null;
  checkOutInstructions?: string | null;
  // Styling
  darkMode: boolean;
}

export function SelfCheckInCard({
  checkInTime,
  selfCheckinEnabled,
  checkinMethod,
  lockboxLocation,
  lockboxCode,
  keypadCode,
  smartLockProvider,
  smartLockInstructions,
  keyLocation,
  entranceInstructions,
  doorCode,
  gateCode,
  buildingAccessInfo,
  floorApartmentInfo,
  checkInInstructions,
  checkOutTime,
  checkOutInstructions,
  darkMode,
}: SelfCheckInCardProps) {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);

  // ── What actually applies to this stay ─────────────────────────────────
  // "personal" = the host hands over the keys (the wizard stores it with
  // self_checkin_enabled=false). Only then do we say "host will meet you" —
  // NOT merely because self check-in is off (instructions may well say
  // "the key is in the mailbox").
  const isPersonal = checkinMethod === 'personal';
  const selfCheckinActive = selfCheckinEnabled && !isPersonal;
  // Codes are shown only for the ACTIVE method. A leftover lockbox code from an
  // old setup must not appear next to the keypad code. Legacy rows with self
  // check-in on but no method chosen keep showing whatever codes exist.
  const methodIs = (m: CheckinMethodType) => selfCheckinActive && (!checkinMethod || checkinMethod === m);
  const showLockboxCode = methodIs('lockbox') && !!lockboxCode;
  const showLockboxLocation = methodIs('lockbox') && !!lockboxLocation;
  const showKeypad = methodIs('keypad') && !!keypadCode;
  const showSmartLock = methodIs('smart_lock') && !!smartLockInstructions;
  const showKeyLocation = methodIs('key_at_location') && !!keyLocation;
  const hasMethodDetails = showLockboxCode || showLockboxLocation || showKeypad || showSmartLock || showKeyLocation;
  const hasBuildingAccess = !!(doorCode || gateCode || floorApartmentInfo || buildingAccessInfo);
  const hasCodes = hasMethodDetails || !!doorCode || !!gateCode;
  // No "how do I get in" information at all (or a method picked without its
  // data, e.g. lockbox without a code) → tell the guest to ask the host,
  // instead of a bare title and a "Confidential" badge.
  const needsHostContact = !isPersonal && !hasMethodDetails && !checkInInstructions && !entranceInstructions;
  const checkInTimeLabel = formatGuestTime(checkInTime);
  const checkOutTimeLabel = formatGuestTime(checkOutTime);

  const hasContent = selfCheckinEnabled || isPersonal || !!checkInInstructions || !!entranceInstructions ||
    hasBuildingAccess || !!checkInTimeLabel || !!checkOutTimeLabel || !!checkOutInstructions;
  if (!hasContent) return null;

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Without self check-in codes the written instructions ARE the key info —
  // give them the emphasised styling.
  const isPersonalHandover = !selfCheckinActive && !!checkInInstructions;

  const getMethodTitle = () => {
    if (isPersonal) return t('guest.selfCheckin.personal', 'Osobna predaja');
    if (!selfCheckinActive) {
      return checkInInstructions
        ? t('guest.selfCheckin.whatToDoOnArrival', 'Što učiniti po dolasku')
        : t('guest.quickActions.arrival', 'Dolazak i odlazak');
    }

    switch (checkinMethod) {
      case 'lockbox':
        return t('guest.selfCheckin.lockbox', 'Lockbox');
      case 'keypad':
        return t('guest.selfCheckin.keypad', 'Tipkovnica na vratima');
      case 'smart_lock':
        return t('guest.selfCheckin.smartLock', 'Pametna brava');
      case 'key_at_location':
        return t('guest.selfCheckin.keyAtLocation', 'Ključ na lokaciji');
      default:
        return t('guest.selfCheckin.title', 'Kako ući');
    }
  };

  const getSubtitle = () => {
    if (isPersonal) {
      return t('guest.selfCheckin.hostWillMeetYou', 'Domaćin će vas dočekati');
    }
    if (checkInTimeLabel) {
      return `${t('guest.houseRules.checkIn', 'Check-in')} ${t('guest.houseRules.from', 'od')} ${checkInTimeLabel}`;
    }
    return t('guest.selfCheckin.privateInfo', 'Privatne informacije');
  };

  const renderCodeBox = (
    label: string,
    code: string,
    fieldName: string,
    icon: React.ReactNode
  ) => (
    <div
      className={`rounded-xl p-4 transition-all duration-300 ${
        darkMode
          ? 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20'
          : 'bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/50 shadow-sm'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <span
            className={`text-xs font-semibold uppercase tracking-wider ${
              darkMode ? 'text-emerald-300' : 'text-emerald-600'
            }`}
          >
            {label}
          </span>
        </div>
        <button
          type="button"
          aria-label={`${t('common.copy', 'Kopiraj')}: ${label}`}
          onClick={() => copyToClipboard(code, fieldName)}
          className={`p-2.5 rounded-lg transition-all duration-200 ${
            copiedField === fieldName
              ? 'bg-green-500/20 text-green-500 scale-110'
              : darkMode
              ? 'hover:bg-slate-600 text-slate-400 hover:text-white'
              : 'hover:bg-emerald-100 text-emerald-400 hover:text-emerald-600'
          }`}
        >
          {copiedField === fieldName ? (
            <Check className="w-4 h-4" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>
      </div>
      <p
        className={`font-mono font-bold text-2xl tracking-widest ${
          darkMode ? 'text-emerald-400' : 'text-emerald-600'
        }`}
      >
        {code}
      </p>
    </div>
  );

  return (
    <div
      className={`relative overflow-hidden rounded-2xl transition-all ${
        darkMode
          ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
          : 'bg-gradient-to-br from-white to-emerald-50/30 border border-emerald-100 shadow-xl shadow-gray-200/80'
      }`}
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-teal-500/10 to-emerald-500/10 rounded-full blur-xl translate-y-1/2 -translate-x-1/2" />

      <div className="relative p-5">
        {/* Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between mb-4"
        >
          <div className="flex items-center gap-3">
            <div className="text-left">
              <h3
                className={`font-bold text-lg tracking-tight ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {getMethodTitle()}
              </h3>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3 h-3 text-emerald-500" />
                <span
                  className={`text-xs ${
                    darkMode ? 'text-slate-400' : 'text-gray-500'
                  }`}
                >
                  {getSubtitle()}
                </span>
              </div>
            </div>
          </div>
          <div
            className={`p-2 rounded-lg ${
              darkMode ? 'text-slate-400' : 'text-gray-400'
            }`}
          >
            {isExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </div>
        </button>

        {isExpanded && (
          <div className="space-y-4">
            {/* Check-in / check-out times — the first thing a guest looks for */}
            {(checkInTimeLabel || checkOutTimeLabel) && (
              <div className={`grid grid-cols-2 gap-3 rounded-xl p-3 ${darkMode ? 'bg-slate-700/40' : 'bg-white shadow-sm border border-gray-100'}`}>
                {checkInTimeLabel && (
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>{t('guest.houseRules.checkIn', 'Check-in')}</p>
                    <p className={`font-bold tabular-nums ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('guest.houseRules.from', 'od')} {checkInTimeLabel}</p>
                  </div>
                )}
                {checkOutTimeLabel && (
                  <div>
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>{t('guest.houseRules.checkOut', 'Check-out')}</p>
                    <p className={`font-bold tabular-nums ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('guest.houseRules.until', 'do')} {checkOutTimeLabel}</p>
                  </div>
                )}
              </div>
            )}

            {/* Confidential Badge — only when there is something secret to guard */}
            {hasCodes && (
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full w-fit ${
                darkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'
              }`}
            >
              <Shield
                className={`w-3.5 h-3.5 ${
                  darkMode ? 'text-emerald-400' : 'text-emerald-600'
                }`}
              />
              <span
                className={`text-xs font-medium ${
                  darkMode ? 'text-emerald-300' : 'text-emerald-700'
                }`}
              >
                {t('guest.selfCheckin.confidential', 'Povjerljivo')}
              </span>
            </div>
            )}

            {/* Personal handover: nudge the guest to share their arrival time */}
            {isPersonal && !checkInInstructions && (
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                {t('guest.selfCheckin.personalHint', 'Javite domaćinu okvirno vrijeme dolaska.')}
              </p>
            )}

            {/* No usable arrival info (or a method chosen without its data) */}
            {needsHostContact && (
              <div className={`rounded-xl p-4 ${
                darkMode
                  ? 'bg-amber-500/10 border border-amber-500/20'
                  : 'bg-amber-50 border border-amber-200/50'
              }`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-5 h-5 mt-0.5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                  <div>
                    <p className={`text-sm font-medium ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                      {t('guest.selfCheckin.contactHost', 'Kontaktirajte domaćina za detalje dolaska')}
                    </p>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      {t('guest.selfCheckin.contactHostDesc', 'Domaćin će vam poslati upute za check-in prije dolaska.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Codes of the ACTIVE self check-in method only */}
            {hasMethodDetails && (
            <div className="space-y-3">
              {/* Lockbox */}
              {(showLockboxCode || showLockboxLocation) && (
                <>
                  {showLockboxCode && lockboxCode && renderCodeBox(
                    t('guest.selfCheckin.lockboxCode', 'Kod za lockbox'),
                    lockboxCode,
                    'lockbox',
                    <Lock className="w-4 h-4 text-emerald-500" />
                  )}
                  {showLockboxLocation && (
                    <div
                      className={`rounded-xl p-4 ${
                        darkMode ? 'bg-slate-700/50' : 'bg-white shadow-sm'
                      }`}
                    >
                      <p
                        className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
                          darkMode ? 'text-slate-400' : 'text-gray-400'
                        }`}
                      >
                        {t('guest.selfCheckin.lockboxLocation', 'Lokacija lockboxa')}
                      </p>
                      <p
                        className={`text-sm ${
                          darkMode ? 'text-slate-200' : 'text-gray-700'
                        }`}
                      >
                        {lockboxLocation}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Keypad */}
              {showKeypad && keypadCode &&
                renderCodeBox(
                  t('guest.selfCheckin.doorCode', 'Kod za vrata'),
                  keypadCode,
                  'keypad',
                  <DoorOpen className="w-4 h-4 text-emerald-500" />
                )}

              {/* Smart Lock */}
              {showSmartLock && (
                <>
                  {smartLockProvider && (
                    <div
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                        darkMode ? 'bg-slate-700/50' : 'bg-gray-50'
                      }`}
                    >
                      <Smartphone className="w-4 h-4 text-emerald-500" />
                      <span
                        className={`text-sm font-medium ${
                          darkMode ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        {smartLockProvider}
                      </span>
                    </div>
                  )}
                  <div
                    className={`rounded-xl p-4 ${
                      darkMode ? 'bg-slate-700/50' : 'bg-white shadow-sm'
                    }`}
                  >
                    <p
                      className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
                        darkMode ? 'text-slate-400' : 'text-gray-400'
                      }`}
                    >
                      {t('guest.selfCheckin.instructions', 'Upute')}
                    </p>
                    <p
                      className={`text-sm whitespace-pre-line ${
                        darkMode ? 'text-slate-200' : 'text-gray-700'
                      }`}
                    >
                      {smartLockInstructions}
                    </p>
                  </div>
                </>
              )}

                {/* Key at Location */}
                {showKeyLocation && (
                  <div
                    className={`rounded-xl p-4 ${
                      darkMode ? 'bg-slate-700/50' : 'bg-white shadow-sm'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Key className="w-4 h-4 text-emerald-500" />
                      <p
                        className={`text-xs font-semibold uppercase tracking-wider ${
                          darkMode ? 'text-slate-400' : 'text-gray-400'
                        }`}
                      >
                        {t('guest.selfCheckin.keyLocation', 'Lokacija ključa')}
                      </p>
                    </div>
                    <p
                      className={`text-sm ${
                        darkMode ? 'text-slate-200' : 'text-gray-700'
                      }`}
                    >
                      {keyLocation}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Building Access Section */}
            {hasBuildingAccess && (
              <div
                className={`pt-4 mt-4 border-t ${
                  darkMode ? 'border-slate-700' : 'border-gray-100'
                }`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Building2
                    className={`w-4 h-4 ${
                      darkMode ? 'text-slate-400' : 'text-gray-400'
                    }`}
                  />
                  <h4
                    className={`text-sm font-semibold ${
                      darkMode ? 'text-slate-300' : 'text-gray-700'
                    }`}
                  >
                    {t('guest.selfCheckin.buildingAccess', 'Pristup zgradi')}
                  </h4>
                </div>

                <div className="space-y-3">
                  {/* Floor/Apartment Info */}
                  {floorApartmentInfo && (
                    <div
                      className={`rounded-lg p-3 ${
                        darkMode ? 'bg-slate-700/30' : 'bg-gray-50'
                      }`}
                    >
                      <p
                        className={`text-sm font-medium ${
                          darkMode ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        {floorApartmentInfo}
                      </p>
                    </div>
                  )}

                  {/* Door and Gate Codes */}
                  <div className="grid grid-cols-2 gap-3">
                    {doorCode && (
                      <div
                        className={`rounded-lg p-3 ${
                          darkMode ? 'bg-slate-700/50' : 'bg-white shadow-sm'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span
                            className={`text-xs ${
                              darkMode ? 'text-slate-400' : 'text-gray-500'
                            }`}
                          >
                            {t('guest.selfCheckin.buildingDoor', 'Ulazna vrata')}
                          </span>
                          <button
                            type="button"
                            aria-label={`${t('common.copy', 'Kopiraj')}: ${t('guest.selfCheckin.buildingDoor', 'Ulazna vrata')}`}
                            onClick={() => copyToClipboard(doorCode, 'door')}
                            className={`p-1 rounded transition-all ${
                              copiedField === 'door'
                                ? 'text-green-500'
                                : darkMode
                                ? 'text-slate-500 hover:text-white'
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                          >
                            {copiedField === 'door' ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                        <p
                          className={`font-mono font-bold ${
                            darkMode ? 'text-white' : 'text-gray-900'
                          }`}
                        >
                          {doorCode}
                        </p>
                      </div>
                    )}
                    {gateCode && (
                      <div
                        className={`rounded-lg p-3 ${
                          darkMode ? 'bg-slate-700/50' : 'bg-white shadow-sm'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span
                            className={`text-xs ${
                              darkMode ? 'text-slate-400' : 'text-gray-500'
                            }`}
                          >
                            {t('guest.selfCheckin.gate', 'Kapija')}
                          </span>
                          <button
                            type="button"
                            aria-label={`${t('common.copy', 'Kopiraj')}: ${t('guest.selfCheckin.gate', 'Kapija')}`}
                            onClick={() => copyToClipboard(gateCode, 'gate')}
                            className={`p-1 rounded transition-all ${
                              copiedField === 'gate'
                                ? 'text-green-500'
                                : darkMode
                                ? 'text-slate-500 hover:text-white'
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                          >
                            {copiedField === 'gate' ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                        <p
                          className={`font-mono font-bold ${
                            darkMode ? 'text-white' : 'text-gray-900'
                          }`}
                        >
                          {gateCode}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Building Access Info */}
                  {buildingAccessInfo && (
                    <p
                      className={`text-sm ${
                        darkMode ? 'text-slate-400' : 'text-gray-600'
                      }`}
                    >
                      {buildingAccessInfo}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Entrance Instructions (How to Enter) */}
            {entranceInstructions && (
              <div
                className={`rounded-xl p-4 ${
                  darkMode
                    ? 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20'
                    : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50'
                }`}
              >
                <p
                  className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
                    darkMode ? 'text-amber-300' : 'text-amber-600'
                  }`}
                >
                  {t('guest.selfCheckin.entranceInstructions', 'Kako uci')}
                </p>
                <p
                  className={`text-sm whitespace-pre-line ${
                    darkMode ? 'text-amber-100' : 'text-amber-900'
                  }`}
                >
                  {entranceInstructions}
                </p>
              </div>
            )}

            {/* General Check-in Instructions (merged from CheckInCard) */}
            {/* For personal handover scenarios, show prominently with different styling */}
            {checkInInstructions && (
              <div
                className={`rounded-xl p-4 ${
                  isPersonalHandover
                    ? darkMode
                      ? 'bg-gradient-to-r from-amber-500/15 to-orange-500/15 border-2 border-amber-500/30'
                      : 'bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300/50'
                    : darkMode
                    ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20'
                    : 'bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200/50'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText
                      className={`w-4 h-4 ${
                        isPersonalHandover
                          ? darkMode ? 'text-amber-400' : 'text-amber-600'
                          : darkMode ? 'text-blue-400' : 'text-blue-600'
                      }`}
                    />
                    <span
                      className={`text-xs font-semibold uppercase tracking-wider ${
                        isPersonalHandover
                          ? darkMode ? 'text-amber-300' : 'text-amber-700'
                          : darkMode ? 'text-blue-300' : 'text-blue-600'
                      }`}
                    >
                      {isPersonalHandover
                        ? t('guest.selfCheckin.importantInstructions', 'Važne upute')
                        : t('guest.selfCheckin.generalInstructions', 'Upute')}
                    </span>
                  </div>
                  <button
                    type="button"
                    aria-label={`${t('common.copy', 'Kopiraj')}: ${isPersonalHandover
                      ? t('guest.selfCheckin.importantInstructions', 'Važne upute')
                      : t('guest.selfCheckin.generalInstructions', 'Upute')}`}
                    onClick={() => copyToClipboard(checkInInstructions, 'instructions')}
                    className={`p-2.5 rounded-lg transition-all duration-200 ${
                      copiedField === 'instructions'
                        ? 'bg-green-500/20 text-green-500 scale-110'
                        : darkMode
                        ? 'hover:bg-slate-600 text-slate-400 hover:text-white'
                        : isPersonalHandover
                        ? 'hover:bg-amber-100 text-amber-400 hover:text-amber-600'
                        : 'hover:bg-blue-100 text-blue-400 hover:text-blue-600'
                    }`}
                  >
                    {copiedField === 'instructions' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p
                  className={`text-sm whitespace-pre-line ${
                    isPersonalHandover
                      ? darkMode ? 'text-amber-100' : 'text-amber-900'
                      : darkMode ? 'text-blue-100' : 'text-blue-900'
                  }`}
                >
                  {checkInInstructions}
                </p>
                {/* Additional hint for personal handover */}
                {isPersonalHandover && (
                  <p
                    className={`mt-3 text-xs flex items-center gap-1.5 ${
                      darkMode ? 'text-amber-400/70' : 'text-amber-600/70'
                    }`}
                  >
                    <AlertCircle className="w-3.5 h-3.5" />
                    {t('guest.selfCheckin.followInstructions', 'Molimo slijedite ove upute za nesmetan dolazak')}
                  </p>
                )}
              </div>
            )}

            {/* Check-out Instructions Section */}
            {checkOutInstructions && (
              <div
                className={`rounded-xl p-4 mt-4 pt-4 border-t ${
                  darkMode
                    ? 'bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 border-t-slate-700'
                    : 'bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200/50 border-t-gray-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Clock
                    className={`w-4 h-4 ${
                      darkMode ? 'text-orange-400' : 'text-orange-600'
                    }`}
                  />
                  <h4
                    className={`text-sm font-semibold ${
                      darkMode ? 'text-orange-300' : 'text-orange-700'
                    }`}
                  >
                    {t('guest.selfCheckin.checkOut', 'Check-out upute')}
                  </h4>
                  {checkOutTimeLabel && (
                    <span
                      className={`ml-auto text-xs font-bold ${
                        darkMode ? 'text-orange-400' : 'text-orange-600'
                      }`}
                    >
                      {t('guest.selfCheckin.until', 'do')} {checkOutTimeLabel}
                    </span>
                  )}
                </div>
                <p
                  className={`text-sm whitespace-pre-line ${
                    darkMode ? 'text-orange-100' : 'text-orange-900'
                  }`}
                >
                  {checkOutInstructions}
                </p>
              </div>
            )}

            {/* Security Notice */}
            {hasCodes && (
            <div
              className={`mt-3 flex items-center gap-2 text-xs ${
                darkMode ? 'text-slate-500' : 'text-gray-400'
              }`}
            >
              <Shield className="w-3 h-3" />
              <span>
                {t('guest.selfCheckin.securityNotice', 'Ove informacije su samo za vas')}
              </span>
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SelfCheckInCard;
